import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { comics, categories } from "@/mocks/comics";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const [activeTag, setActiveTag] = useState("全部");
  const [sortBy, setSortBy] = useState("popular");

  const categoryComics = comics.filter((c) => {
    if (!category) return true;
    return c.category === category.name;
  });

  const filteredComics = activeTag === "全部"
    ? categoryComics
    : categoryComics.filter((c) => c.tags.includes(activeTag));

  const sortedComics = [...filteredComics].sort((a, b) => {
    if (sortBy === "popular") return b.views - a.views;
    if (sortBy === "latest") return new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime();
    if (sortBy === "rating") return b.rating - a.rating;
    return 0;
  });

  const allTags = ["全部", ...new Set(comics.flatMap((c) => c.tags))];

  useEffect(() => {
    setActiveTag("全部");
    setSortBy("popular");
  }, [slug]);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-2xl text-foreground-600"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-950 mb-2">分类未找到</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />

      <main>
        {/* Breadcrumb */}
        <nav className="pt-20 pb-0" aria-label="面包屑导航">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-4">
            <ol className="flex items-center gap-2 text-xs text-foreground-600">
              <li><Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link></li>
              <li><span className="text-foreground-400">/</span></li>
              <li><Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">分类浏览</Link></li>
              <li><span className="text-foreground-400">/</span></li>
              <li><span className="text-foreground-950 font-medium">{category.name}</span></li>
            </ol>
          </div>
        </nav>

        {/* Header */}
        <section className="bg-background-50 py-8" aria-labelledby="category-title">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6 text-center">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 border border-background-300 rounded-full text-xs font-medium text-foreground-600 mb-4">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${category.icon} text-sm`}></i>
              </div>
              分类浏览
            </span>
            <h1 id="category-title" className="text-3xl md:text-4xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
              {category.name}
            </h1>
            <p className="text-sm text-foreground-600">共 {category.count} 部漫画作品，持续更新中</p>
          </div>
        </section>

        {/* Filters */}
        <section className="bg-background-50 pb-4">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-4 border-b border-background-200/70">
              <div className="flex items-center gap-2 flex-wrap">
                {allTags.slice(0, 8).map((tag) => (
                  <button
                    key={tag}
                    onClick={() => setActiveTag(tag)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeTag === tag
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                {[
                  { key: "popular", label: "最热门" },
                  { key: "latest", label: "最新更新" },
                  { key: "rating", label: "评分最高" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      sortBy === opt.key
                        ? "bg-secondary-500 text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Comic Grid */}
        <section className="bg-background-50 py-10" aria-label={`${category.name}漫画列表`}>
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop="">
              {sortedComics.map((comic, idx) => (
                <Link
                  key={comic.id}
                  to={`/article/${comic.slug}`}
                  className="group cursor-pointer"
                >
                  <div className="bg-background-100 rounded-lg overflow-hidden transition-all duration-300 hover:scale-[1.02]">
                    <div className="relative h-[320px] overflow-hidden">
                      <img
                        src={comic.cover}
                        alt={`${comic.title} - 韩漫下拉式免费阅读`}
                        title={`${comic.title} - 无删减韩漫下拉式在线`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading={idx < 3 ? "eager" : "lazy"}
                      />
                      <div className="absolute top-3 left-3">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-accent-500 text-background-50 text-xs font-semibold rounded-full">
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-star-fill text-[10px]"></i>
                          </div>
                          {comic.rating}
                        </span>
                      </div>
                      <div className="absolute top-3 right-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${comic.status === "连载中" ? "bg-primary-500/90 text-background-50" : "bg-foreground-700/80 text-background-50"}`}>
                          {comic.status}
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="text-xs text-accent-500 font-medium mb-1.5">{comic.category}</p>
                      <h3 className="text-base font-semibold text-foreground-950 mb-1 group-hover:text-primary-500 transition-colors" style={{ fontFamily: "var(--font-heading)" }}>
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-600 line-clamp-2 mb-3">{comic.description}</p>
                      <div className="flex items-center justify-between text-xs text-foreground-600">
                        <span>{comic.author}</span>
                        <span>{comic.chapters}话</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
              {/* Visual break - middle card */}
              {sortedComics.length >= 2 && (
                <div className="hidden lg:flex items-center justify-center bg-foreground-950 rounded-lg p-8 min-h-[320px]">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-500/20">
                      <i className="ri-fire-line text-2xl text-accent-500"></i>
                    </div>
                    <p className="text-accent-500 font-semibold text-lg mb-2">海量韩漫</p>
                    <p className="text-foreground-400 text-sm">持续更新中</p>
                    <p className="text-foreground-500 text-xs mt-1">下拉式免费阅读</p>
                  </div>
                </div>
              )}
            </div>
            {sortedComics.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-600"></i>
                </div>
                <p className="text-foreground-600">暂无该分类的漫画</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: `${category.name} - 韩漫画网`,
          description: `韩漫画网${category.name}分类，共${category.count}部无删减韩漫下拉式免费在线阅读`,
          url: `http://xxjiaxiang.com/category/${slug}`,
          isPartOf: {
            "@type": "WebSite",
            name: "韩漫画网",
            url: "http://xxjiaxiang.com/",
          },
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "http://xxjiaxiang.com/" },
            { "@type": "ListItem", position: 2, name: "分类浏览", item: "http://xxjiaxiang.com/" },
            { "@type": "ListItem", position: 3, name: category.name, item: `http://xxjiaxiang.com/category/${slug}` },
          ],
        })}
      </script>
    </>
  );
}